'use client';

import { ColumnDef } from '@tanstack/react-table';
import { Package, DollarSign, Ellipsis, Tag } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ProductTableImageCell } from '@/components/products/ProductTableImageCell';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { formatMoney } from "@/lib/money";
import { getCurrencyOption } from '@/lib/currency/currencyOptionsKeys';
import { Product } from '@tagadapay/crm-entities';
import { ProductResourceBadge } from '@/components/products/ProductResourceBadge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { ContentDisplay } from '@/components/ui/content-display';

interface GetProductsTableColumnsProps {
  storeId: string;
  onEditProduct: (productId: string) => void;
  onDeleteProduct: (productId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  storeRoutes: any;
  displayCurrency?: string;
  onOpenProductImage?: (productId: string) => void;
}

export function getProductsTableColumns({
  storeId,
  onEditProduct,
  onDeleteProduct,
  t,
  storeRoutes,
  displayCurrency,
  onOpenProductImage,
}: GetProductsTableColumnsProps): ColumnDef<Product.Product>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    {
      id: 'image',
      accessorKey: 'image',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.image')} />,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <ProductTableImageCell
            src={product.image}
            alt={product.name}
            placeholderLabel={t('table.imagePreview.noImage')}
            zoomLabel={t('table.imagePreview.zoomImage')}
            onPreview={product.image ? () => onOpenProductImage?.(product.id) : undefined}
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 60,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.name')} />,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="min-w-0">
            <Link to={storeRoutes.PRODUCT_DETAILS(product.id)}>
              <div className="truncate font-medium hover:underline">{product.name}</div>
            </Link>
            {product.description && (
              <div className="text-muted-foreground max-w-[400px] text-xs">
                <ContentDisplay content={product.description} maxLength={75} />
              </div>
            )}
          </div>
        );
      },
      meta: {
        label: t('table.columns.name'),
        placeholder: t('table.filters.searchPlaceholder'),
        variant: 'text',
        icon: () => <Package />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'price',
      accessorKey: 'price',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.price')} />,
      cell: ({ row }) => {
        const product = row.original;
        const resolved = getCurrencyOption(product.price?.currencyOptions, displayCurrency);
        if (!resolved || resolved.option.amount <= 0) {
          return <span className="text-muted-foreground">{t('table.noPrice')}</span>;
        }

        return (
          <div className="">
            <div className="font-medium">{formatMoney(resolved.option.amount, resolved.currency)}</div>
            {product.price.recurring && (
              <div className="text-muted-foreground text-xs">
                {product.price.interval && product.price.intervalCount
                  ? t('table.perInterval', {
                      count: product.price.intervalCount,
                      interval: product.price.interval,
                    })
                  : t('table.recurring')}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
      meta: { label: t('table.columns.price') },
    },
    {
      id: 'type',
      accessorKey: 'price.recurring',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.type')} />,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <Badge variant={product.price?.recurring ? 'secondary' : 'outline'}>
            {product.price?.recurring ? t('table.columns.subscription') : t('table.columns.oneTime')}
          </Badge>
        );
      },
      enableSorting: false,
    },
    // Product ID (moved to end, copyable)
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.id')} />,
      cell: ({ row }) => {
        const product = row.original;
        const displayValue = `${product.id.substring(0, 8)}...`;
        return <CopyableResourceBadge name={t('table.columns.id')} value={displayValue} copyValue={product.id} />;
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
      meta: { label: t('table.columns.id') },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem onClick={() => onEditProduct(product.id)}>
                {t('table.actions.editProduct')}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={storeRoutes.PRODUCT_DETAILS(product.id)}>{t('table.actions.viewDetails')}</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDeleteProduct(product.id)}
                className="text-destructive focus:text-destructive"
              >
                {t('table.actions.deleteProduct')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
